import {
  type ContextSourceRow,
  useCachedReferenceData,
  useContextSources,
  useProjectDocuments,
} from "@/hooks/useReferenceData"
import { useProjectSettings } from "@/hooks/useProjectSettings"
import { useGovernmentLayerSettings } from "@/hooks/useGovernmentLayer"
import {
  formatBufferMetres,
  formatContextSourceLabel,
  GOVERNMENT_LAYERS,
} from "@fieldbook/common/utils"
import {
  DBCA_CONTEXT_CATEGORY_LABELS,
  type DBCAContextCategory,
  dbcaContextCategory,
  isDBCASiteSourceKind,
} from "@fieldbook/common/types"
import { Badge } from "@fieldbook/ui/badge"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowLeft,
  ChevronRight,
  FileText,
  Layers,
  MapPin,
  RefreshCw,
} from "lucide-react"

export const Route = createFileRoute("/_private/projects/$id/data/context/")({
  component: ContextSourcesList,
})

function SyncBadges({ synced, onMap }: { synced: boolean; onMap: boolean }) {
  return (
    <>
      {synced && (
        <Badge variant="outline" className="gap-1 text-xs">
          <RefreshCw size={10} />
          Synced
        </Badge>
      )}
      {onMap && (
        <Badge variant="outline" className="gap-1 text-xs">
          <MapPin size={10} />
          On map
        </Badge>
      )}
    </>
  )
}

function GovernmentLayerRow({
  projectId,
  layerId,
}: {
  projectId: string
  layerId: string
}) {
  const { def, isSynced, isOnMap, bufferMetres } = useGovernmentLayerSettings(
    projectId,
    layerId,
  )
  if (!def) return null

  return (
    <Link
      to="/projects/$id/data/context/government/$layerId"
      params={{ id: projectId, layerId }}
      className="border-muted flex items-center justify-between border-t p-2"
    >
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-sm font-medium">
          <Layers size={14} className="text-muted-foreground" />
          {def.title}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs">
            {bufferMetres > 0
              ? `${formatBufferMetres(bufferMetres)} buffer`
              : "No buffer"}
          </span>
          <SyncBadges synced={isSynced} onMap={isOnMap} />
        </div>
      </div>
      <ChevronRight size={20} className="text-muted-foreground" />
    </Link>
  )
}

// Imported sources are grouped so DBCA flora, fauna and communities read as
// separate kinds of data; anything else (external imports) comes last.
const SOURCE_GROUPS: { category: DBCAContextCategory | null; label: string }[] =
  [
    { category: "flora", label: DBCA_CONTEXT_CATEGORY_LABELS.flora },
    { category: "fauna", label: DBCA_CONTEXT_CATEGORY_LABELS.fauna },
    {
      category: "communities",
      label: DBCA_CONTEXT_CATEGORY_LABELS.communities,
    },
    { category: null, label: "Other imported data" },
  ]

function sourceSummary(source: ContextSourceRow) {
  if (source.kind === "dbca_tec_pec") return `${source.feature_count} polygons`
  if (isDBCASiteSourceKind(source.kind)) return `${source.feature_count} sites`
  return `${source.feature_count} locations • ${source.species_count} species`
}

function ContextSourcesList() {
  const { id: projectId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: sources } = useContextSources(projectId)
  // Linked organisation documents (via the project_document junction) list
  // alongside the project-scoped context sources.
  const { data: documents } = useProjectDocuments(projectId)
  // Kept mounted so feature counts for synced sources stay warm for the detail.
  useCachedReferenceData(projectId)
  const { settings } = useProjectSettings(projectId)
  const syncedIds = settings.syncedContextSourceIds ?? []
  const mapIds = settings.mapContextSourceIds ?? []
  const syncedDocumentIds = settings.syncedDocumentIds ?? []
  const mapDocumentIds = settings.mapDocumentIds ?? []

  const hasItems = Boolean(
    (sources && sources.length > 0) || (documents && documents.length > 0),
  )

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="mb-2 mt-2 space-y-2">
        <button
          onClick={() =>
            navigate({ to: "/projects/$id/data", params: { id: projectId } })
          }
          className="text-muted-foreground flex items-center gap-1 text-sm focus-visible:outline-0"
        >
          <ArrowLeft size={12} />
          <span>All data</span>
        </button>
        <span className="text-muted-foreground text-lg">Context</span>
      </div>

      {!hasItems ? (
        <div className="border-muted flex flex-col items-center gap-4 border-y py-6 text-center">
          <p className="text-muted-foreground text-sm">
            No project context data has been imported.
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          {SOURCE_GROUPS.map(({ category, label }) => {
            const groupSources = sources.filter(
              (source) => dbcaContextCategory(source.kind) === category,
            )
            if (groupSources.length === 0) return null
            return (
              <div key={label} className="flex flex-col">
                <span className="text-muted-foreground mb-1 mt-4 text-sm">
                  {label}
                </span>
                <div className="border-muted flex flex-col border-b">
                  {groupSources.map((source) => (
                    <Link
                      key={source.id}
                      to="/projects/$id/data/context/$sourceId"
                      params={{ id: projectId, sourceId: source.id }}
                      className="border-muted flex items-center justify-between border-t p-2"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">
                          {formatContextSourceLabel(
                            source.kind,
                            source.source_filename ?? source.source_org_name,
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-xs">
                            {sourceSummary(source)}
                          </span>
                          <SyncBadges
                            synced={syncedIds.includes(source.id)}
                            onMap={mapIds.includes(source.id)}
                          />
                        </div>
                      </div>
                      <ChevronRight
                        size={20}
                        className="text-muted-foreground"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            )
          })}

          {documents.length > 0 && (
            <span className="text-muted-foreground mb-1 mt-4 text-sm">
              Documents
            </span>
          )}
          {documents.map((document) => (
            <Link
              key={document.id}
              to="/projects/$id/data/context/document/$documentId"
              params={{ id: projectId, documentId: document.id }}
              className="border-muted flex items-center justify-between border-t p-2"
            >
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-1 text-sm font-medium">
                  <FileText size={14} className="text-muted-foreground" />
                  {document.title ?? document.source_filename ?? "Document"}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    PDF document • {document.occurrence_count} records
                  </span>
                  <SyncBadges
                    synced={syncedDocumentIds.includes(document.id)}
                    onMap={mapDocumentIds.includes(document.id)}
                  />
                </div>
              </div>
              <ChevronRight size={20} className="text-muted-foreground" />
            </Link>
          ))}
        </div>
      )}

      {/* Government datasets are live public layers, available to every
        project without an import. */}
      <span className="text-muted-foreground mb-1 mt-6 text-sm">
        Government datasets
      </span>
      <div className="border-muted flex flex-col border-b">
        {GOVERNMENT_LAYERS.map((def) => (
          <GovernmentLayerRow
            key={def.id}
            projectId={projectId}
            layerId={def.id}
          />
        ))}
      </div>
    </div>
  )
}
